import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  all: {
    title: '香菱全部作品列表 — 星空传媒香菱出演作品全集',
    description: '星空传媒香菱全部作品完整列表，涵盖剧情片、专访访谈、精选合集、幕后花絮等所有类型。快速浏览香菱出演的每一部精彩作品，收藏必备。',
    keywords: '香菱出演作品列表,星空传媒香菱全集,香菱全部作品,星空传媒女优作品合集,香菱最新作品',
  },
  drama: {
    title: '香菱剧情片作品 — 星空传媒香菱主演剧情片合集',
    description: '星空传媒香菱主演的剧情片作品合集，包含悬疑、爱情、动作、温情等多种类型的剧情片。每部作品都有详细剧情解析，帮助粉丝深入了解香菱的演技魅力。',
    keywords: '香菱剧情片,星空传媒香菱剧情,香菱主演电影,星空传媒剧情片,香菱电影作品',
  },
  interview: {
    title: '香菱专访访谈 — 星空传媒香菱深度对话实录',
    description: '星空传媒香菱独家专访与深度访谈合集，听香菱分享表演心得、生活感悟和职业心路历程。了解银幕之外的香菱真实一面。',
    keywords: '香菱专访,星空传媒香菱访谈,香菱深度对话,香菱幕后故事,香菱个人采访',
  },
  collection: {
    title: '香菱精选合集 — 星空传媒香菱作品年度精选',
    description: '星空传媒精心整理的香菱作品精选合集，包含年度精华集锦、高光时刻回顾等。一站式欣赏香菱最精彩的表演瞬间。',
    keywords: '香菱精选合集,星空传媒香菱集锦,香菱作品精华,香菱高光时刻,香菱年度回顾',
  },
  latest: {
    title: '香菱最新作品 — 星空传媒香菱最新发布作品',
    description: '星空传媒香菱最新发布的影视作品，第一时间获取香菱新作动态。紧跟香菱最新出演计划，不错过任何一部精彩作品。',
    keywords: '香菱最新作品,星空传媒香菱新作,香菱最新发布,香菱2024新作品,星空传媒最新出品',
  },
  'behind-scenes': {
    title: '香菱幕后花絮 — 星空传媒香菱拍摄现场实录',
    description: '星空传媒香菱作品幕后花絮合集，真实记录香菱在拍摄现场的专业表现。从武术特训到剧本围读，全面展现作品背后的故事。',
    keywords: '香菱幕后花絮,星空传媒拍摄现场,香菱片场花絮,香菱拍摄记录,星空传媒幕后故事',
  },
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const currentSlug = categories.some((c) => c.slug === slug) ? slug : 'all';

  useEffect(() => {
    if (currentSlug !== slug) {
      navigate(`/category/${currentSlug}`, { replace: true });
    }
  }, [slug, currentSlug, navigate]);

  const currentArticles = useMemo(() => {
    const list = getArticlesByCategory(currentSlug);
    if (sortBy === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'views') return [...list].sort((a, b) => b.views - a.views);
    return [...list].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
  }, [currentSlug, sortBy]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const meta = categoryMeta[currentSlug] || categoryMeta.all;

  const categoryMap = Object.fromEntries(categories.map((c) => [c.slug, c.name]));

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://liveorbit.github.io/category/${currentSlug}`}
      />

      <div className="pt-20 pb-8 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '全部作品' },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-100">
              {currentCategory?.name || '全部作品'}
            </h1>
            <p className="mt-2 text-sm text-foreground-400 max-w-2xl">
              {meta.description.slice(0, 80)}...
            </p>
          </div>
        </div>
      </div>

      <div className="py-8 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-300 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Sort */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-foreground-400">
              共 <strong className="text-foreground-200">{currentArticles.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
              {[
                { value: 'latest', label: '最新' },
                { value: 'rating', label: '评分' },
                { value: 'views', label: '热度' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value as typeof sortBy)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.value
                      ? 'bg-background-50 text-foreground-100'
                      : 'text-foreground-500 hover:text-foreground-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Article Grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {currentArticles.map((article) => (
              <ArticleCard
                key={article.id}
                {...article}
                categoryName={categoryMap[article.category] || article.category}
              />
            ))}
          </div>

          {currentArticles.length === 0 && (
            <div className="py-20 text-center">
              <i className="ri-inbox-line text-4xl text-foreground-500"></i>
              <p className="mt-3 text-foreground-400">该分类暂无作品</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}